import { useState } from "react";
import { Plane, Car, BedDouble, Globe, ChevronDown, Palmtree, Menu, X, Users, UserCircle, Wifi, Shield, Landmark, Music, Wrench, ShoppingCart } from "lucide-react";
import travelBoostLogo from "@/assets/travel-boost-logo.jpg";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLocale, languages } from "@/contexts/LocaleContext";
import { useCart } from "@/contexts/CartContext";
import { cn } from "@/lib/utils";
import { UserMenuButton } from "./UserMenuButton";

const FlagIcon = ({ src, alt, fallback }: { src?: string; alt: string; fallback: string }) => {
  // Some devices don't render flag emojis reliably; prefer our SVGs with an emoji fallback.
  if (!src) return <span className="text-lg leading-none">{fallback}</span>;

  return (
    <img
      src={src}
      alt={alt}
      className="h-4 w-6 shrink-0 rounded-[2px] border border-border/50 object-cover"
      loading="eager"
      decoding="async"
    />
  );
};

const getWhatsappOptions = (lang: string) => [
  {
    icon: UserCircle,
    labelKey: "whatsapp.consult_matan",
    descKey: "whatsapp.consult_desc",
    href: "https://wa.me/972505516701",
  },
  {
    icon: Users,
    labelKey: "whatsapp.secret_tips",
    descKey: "whatsapp.secret_desc",
    href: lang === "he"
      ? "https://chat.whatsapp.com/LGPI6BAwYrS5M5kGSiNgLQ"
      : lang === "es"
        ? "https://chat.whatsapp.com/JeFFLr7IVIQIHkjgp8DwKQ?mode=gi_t"
        : "https://chat.whatsapp.com/BdTImDgFZfqEns7EaxWmiS?mode=gi_t",
  },
];

// Social media links with brand colors
const socialLinks = [
  {
    name: "WhatsApp",
    color: "#25D366",
    hasMenu: true,
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    ),
  },
  {
    name: "Facebook",
    color: "#1877F2",
    href: "https://www.facebook.com/profile.php?id=61588075077612",
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
  },
  {
    name: "Instagram",
    color: "#E1306C",
    href: "https://instagram.com",
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
      </svg>
    ),
  },
  {
    name: "TikTok",
    color: "#000000",
    href: "https://tiktok.com",
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
      </svg>
    ),
  },
  {
    name: "YouTube",
    color: "#FF0000",
    href: "https://youtube.com",
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    ),
  },
];

export const Header = () => {
  const { t } = useTranslation();
  const { language, setLanguage, isRTL } = useLocale();
  const { itemCount } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [whatsappMenuOpen, setWhatsappMenuOpen] = useState(false);

  // Services with translations
  const services = [
    { icon: BedDouble, labelKey: "services_bar.hotel", href: "/hotels" },
    { icon: Car, labelKey: "services_bar.car", href: "/cars" },
    { icon: Plane, labelKey: "services_bar.flights", href: "/flights" },
    { icon: Palmtree, labelKey: "services_bar.packages", href: "/packages" },
    { icon: Wifi, labelKey: "services_bar.esim", href: "/esim" },
    { icon: Shield, labelKey: "services_bar.insurance", href: "/insurance" },
    { icon: Landmark, labelKey: "services_bar.attractions", href: "/attractions" },
    { icon: Music, labelKey: "services_bar.tickets", href: "/tickets" },
  ];

  return (
    <header className="sticky top-0 z-50 w-full" dir={isRTL ? "rtl" : "ltr"}>
      {/* Services Bar - Desktop Only */}
      <div className="hidden md:block bg-primary/5 backdrop-blur-sm border-b border-border/30 overflow-x-auto">
        <div className="container flex items-center justify-center py-2 relative">
          {/* Service Links - Centered */}
          <div className="flex items-center gap-4 lg:gap-6">
            {services.map((service) => (
              <Link
                key={service.href}
                to={service.href}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors whitespace-nowrap"
              >
                <service.icon className="h-3.5 w-3.5" />
                <span>{t(service.labelKey)}</span>
              </Link>
            ))}
          </div>

          {/* Cart + User Menu + Language Switcher - Absolute Right */}
          <div className={cn(
            "absolute top-1/2 -translate-y-1/2 flex items-center gap-3",
            isRTL ? "left-4" : "right-4"
          )}>
            {/* Cart Button */}
            <Link to="/cart" className="relative">
              <Button variant="ghost" size="sm" className="gap-1.5 hover:bg-primary/10 h-8 px-3">
                <ShoppingCart className="h-4 w-4" />
                <span className="text-sm font-medium">{t("nav.cart", "סל")}</span>
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-secondary text-secondary-foreground text-xs font-bold flex items-center justify-center">
                    {itemCount > 9 ? "9+" : itemCount}
                  </span>
                )}
              </Button>
            </Link>
            
            {/* User Menu Button */}
            <UserMenuButton />
            
            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 hover:bg-primary/10 h-8 px-3">
                  <FlagIcon src={language.flagSrc} alt={`${language.label} flag`} fallback={language.flag} />
                  <span className="text-sm font-medium uppercase">{language.code}</span>
                  <ChevronDown className="h-3 w-3 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align={isRTL ? "start" : "end"} className="backdrop-blur-xl bg-background/95 border-border/50 min-w-[160px]">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang)}
                    className={cn(
                      "gap-3 cursor-pointer py-2.5",
                      language.code === lang.code && "bg-primary/10"
                    )}
                  >
                    <FlagIcon src={lang.flagSrc} alt={`${lang.label} flag`} fallback={lang.flag} />
                    <span className="font-medium">{lang.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Main Header - Glassmorphism */}
      <div className="backdrop-blur-xl bg-background/90 border-b border-border/30 shadow-sm">
        <div className={cn(
          "flex items-center justify-between w-full py-4 px-6",
          !isRTL && "flex-row-reverse"
        )}>
          
          {/* SECTION 1: Logo (always on brand side) */}
          <div className="hidden lg:flex items-center shrink-0">
            <Link to="/" dir="ltr">
              <img src={travelBoostLogo} alt="Travel Boost" className="h-28 w-auto object-contain mix-blend-multiply" />
            </Link>
          </div>

          {/* Mobile: Menu Button - Hidden since we use bottom nav */}
          <div className={cn(
            "hidden", // Hide hamburger on mobile - we use sticky bottom nav instead
            isRTL ? "order-2" : "order-1"
          )}>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>

          {/* Mobile: Logo Centered */}
          <div className="lg:hidden flex-1 flex items-center justify-center order-2">
            <Link to="/" dir="ltr">
              <img src={travelBoostLogo} alt="Travel Boost" className="h-20 w-auto object-contain mix-blend-multiply" />
            </Link>
          </div>

          {/* SECTION 2: All Navigation Links (Desktop) - Centered as one group */}
          <nav className="hidden lg:flex items-center justify-center gap-6 flex-1">
            <Link 
              to="/" 
              className="font-bold text-foreground hover:text-primary transition-colors whitespace-nowrap"
            >
              {t("nav.home")}
            </Link>
            <Link 
              to="/destinations/israel" 
              className="font-bold text-foreground hover:text-primary transition-colors whitespace-nowrap"
            >
              {t("nav.destinations_israel")}
            </Link>
            <Link 
              to="/destinations/world" 
              className="font-bold text-foreground hover:text-primary transition-colors whitespace-nowrap"
            >
              {t("nav.destinations_world")}
            </Link>
            <Link 
              to="/services" 
              className="font-bold text-foreground hover:text-primary transition-colors whitespace-nowrap"
            >
              {t("nav.services")}
            </Link>
            <Link 
              to="/tools" 
              className="font-bold text-foreground hover:text-primary transition-colors whitespace-nowrap"
            >
              {t("nav.tools")}
            </Link>
            <Link 
              to="/blog" 
              className="font-bold text-foreground hover:text-primary transition-colors whitespace-nowrap"
            >
              {t("nav.blog")}
            </Link>
            <Link 
              to="/business-content"
              className="font-bold text-foreground hover:text-primary transition-colors whitespace-nowrap"
            >
              {t("nav.business_content")}
            </Link>
            <Link 
              to="/about" 
              className="font-bold text-foreground hover:text-primary transition-colors whitespace-nowrap"
            >
              {t("nav.about")}
            </Link>
            <Link 
              to="/contact" 
              className="font-bold text-foreground hover:text-primary transition-colors whitespace-nowrap"
            >
              {t("nav.contact")}
            </Link>
          </nav>

          {/* SECTION 3: Social Icons Only (Desktop) */}
          <div className="hidden lg:flex items-center gap-3 shrink-0">
            {socialLinks.map((social) => (
              social.hasMenu ? (
                <div key={social.name} className="relative">
                  <button
                    onClick={() => setWhatsappMenuOpen(!whatsappMenuOpen)}
                    className="h-9 w-9 rounded-full flex items-center justify-center transition-all hover:scale-110 shadow-md"
                    style={{ backgroundColor: social.color, color: "white" }}
                    title={social.name}
                  >
                    {social.icon}
                  </button>
                  
                  {/* WhatsApp Menu Popup - Conditional rendering to reduce DOM */}
                  {whatsappMenuOpen && (
                    <div
                      className={cn(
                        "absolute top-12 w-64 bg-background/95 backdrop-blur-xl rounded-2xl shadow-lg border border-border/50 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200",
                        isRTL ? "right-0" : "left-0"
                      )}
                    >
                      <div className="p-3 space-y-2">
                        {getWhatsappOptions(language.code).map((option) => (
                          <a
                            key={option.labelKey}
                            href={option.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-3 rounded-xl hover:bg-accent transition-colors group"
                            onClick={() => setWhatsappMenuOpen(false)}
                          >
                            <div className="h-10 w-10 rounded-full bg-[#25D366]/10 flex items-center justify-center group-hover:bg-[#25D366]/20 transition-colors">
                              <option.icon className="h-5 w-5 text-[#25D366]" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{t(option.labelKey)}</p>
                              <p className="text-xs text-muted-foreground">{t(option.descKey)}</p>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-9 w-9 rounded-full flex items-center justify-center transition-all hover:scale-110 shadow-md"
                  style={{ backgroundColor: social.color, color: "white" }}
                  title={social.name}
                >
                  {social.icon}
                </a>
              )
            ))}
          </div>

          {/* Mobile Language Switcher */}
          <div className={cn(
            "lg:hidden flex items-center",
            isRTL ? "order-3" : "order-3"
          )}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1.5 hover:bg-primary/10 px-2">
                  <FlagIcon src={language.flagSrc} alt={`${language.label} flag`} fallback={language.flag} />
                  <span className="text-sm font-medium uppercase">{language.code}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align={isRTL ? "start" : "end"} className="backdrop-blur-xl bg-background/95 border-border/50">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang)}
                    className={cn(
                      "gap-3 cursor-pointer py-2.5",
                      language.code === lang.code && "bg-primary/10"
                    )}
                  >
                    <FlagIcon src={lang.flagSrc} alt={`${lang.label} flag`} fallback={lang.flag} />
                    <span className="font-medium">{lang.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Mobile Menu Dropdown - Conditional rendering to reduce DOM size */}
        {mobileMenuOpen && (
          <div className="lg:hidden overflow-hidden transition-all duration-300 border-t border-border/30 py-4 animate-in slide-in-from-top-2">
            <div className="container space-y-4">
              {/* Navigation Links */}
              <nav className="space-y-1" role="navigation" aria-label="Mobile menu">
                <Link 
                  to="/" 
                  className="block font-bold text-foreground hover:text-primary transition-colors py-3 min-h-[44px] touch-target"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t("nav.home")}
                </Link>
                
                <Link 
                  to="/destinations/israel" 
                  className="block font-bold text-foreground hover:text-primary transition-colors py-3 min-h-[44px] touch-target"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t("nav.destinations_israel")}
                </Link>

                <Link 
                  to="/destinations/world" 
                  className="block font-bold text-foreground hover:text-primary transition-colors py-3 min-h-[44px] touch-target"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t("nav.destinations_world")}
                </Link>

                <div className="border-t border-border/30 pt-3 space-y-1">
                  <Link 
                    to="/services" 
                    className="block font-bold text-foreground hover:text-primary transition-colors py-3 min-h-[44px] touch-target"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t("nav.services")}
                  </Link>
                  <Link 
                    to="/tools" 
                    className="block font-bold text-foreground hover:text-primary transition-colors py-3 min-h-[44px] touch-target"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t("nav.tools")}
                  </Link>
                  <Link 
                    to="/blog" 
                    className="block font-bold text-foreground hover:text-primary transition-colors py-3 min-h-[44px] touch-target"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t("nav.blog")}
                  </Link>
                  <Link 
                    to="/business-content" 
                    className="block font-bold text-foreground hover:text-primary transition-colors py-3 min-h-[44px] touch-target"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t("nav.business_content")}
                  </Link>
                  <Link 
                    to="/about" 
                    className="block font-bold text-foreground hover:text-primary transition-colors py-3 min-h-[44px] touch-target"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t("nav.about")}
                  </Link>
                  <Link 
                    to="/contact" 
                    className="block font-bold text-foreground hover:text-primary transition-colors py-3 min-h-[44px] touch-target"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t("nav.contact")}
                  </Link>
                </div>
              </nav>

              {/* Social Links - Mobile */}
              <div className="border-t border-border/30 pt-4">
                <div className="flex items-center justify-center gap-4">
                  {socialLinks.map((social) => (
                    social.hasMenu ? (
                      <a
                        key={social.name}
                        href="https://wa.me/972501234567"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="h-11 w-11 rounded-full flex items-center justify-center shadow-md touch-target"
                        style={{ backgroundColor: social.color, color: "white" }}
                        title={social.name}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {social.icon}
                      </a>
                    ) : (
                      <a
                        key={social.name}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="h-11 w-11 rounded-full flex items-center justify-center shadow-md touch-target"
                        style={{ backgroundColor: social.color, color: "white" }}
                        title={social.name}
                      >
                        {social.icon}
                      </a>
                    )
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
